'use client';

import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import type { EmblaOptionsType } from 'embla-carousel-react';

import type { Product } from '@hyex/dxp/types/store/Product/Product';
import type { ProductVariantType } from '@hyex/dxp/types/domain/ProductVariantType';
import ImageCarousel from '@hyex/modules/shared/Carousels/ImageCarousel';
import UltraCartService from '@hyex/modules/portal/ultracart/uc.cart.service';
import useIsMobile from '@hyex/utils/hooks/useIsMobile';

import {
  AUTO_SHIP1,
  AUTO_SHIP3,
  ONE_TIME1,
  ONE_TIME3,
  ONE_TIME6,
} from '@hyex/modules/portal/constants';
import { createProductVariantsCarousel } from '@hyex/modules/portal/domain/domain.utils';
import ProductImage from '@hyex/modules/shared/Product/common/ProductImage';
import ProductAllergens from './allergens';
import ProductOrderForm from './order-form';
import { OrderType } from '../../../../../dxp/src/types/domain/OrderType';

type PropTypes = {
  product: Product;
};

//TODO: SSR in future!
const ProductOrderController: FC<PropTypes> = ({ product }) => {
  const styles = {
    mobile: {
      slider: 'carousel-main-slide-img-s',
    },
    desktop: {
      slider: 'carousel-main-slide-img',
    },
  };

  const isMobile = useIsMobile(1000);

  const prevOrderType = useRef('');

  const prevVariantQuantity = useRef('');

  const [selectedIndex, setSelectedIndex] = useState(0);

  const [orderType, setOrderType] = useState('');

  const [quantity, setQuantity] = useState(1);

  const [showSupplementImage, setShowSupplementImage] = useState(false);

  const [showInstructionImage, setShowInstructionImage] = useState(false);

  const [disabledSkus, setDisabledSkus] = useState<Record<string, boolean>>({});

  const options: EmblaOptionsType = {
    containScroll: 'keepSnaps',
    axis: 'y',
    dragFree: true,
    breakpoints: {
      '(max-width: 1000px)': { axis: 'x' },
    },
  };

  const {
    defaultOneTimeVariant,
    defaultAutoshipVariant,
    oneTimeVariants,
    autoshipVariants,
    oneTimeSlides,
    autoshipSlides,
    oneTimeImages,
    autoshipImages,
    hasSupplementImage,
    hasInstructionImage,
  } = useMemo(() => createProductVariantsCarousel(product), []);

  const orderTypes: OrderType[] = useMemo(
    () => [
      {
        id: ONE_TIME1,
        quantity: 1,
        text: '1 Bottle Trial Offer + Shipping',
        className: 'radio-block once',
        autoship: false,
        html: '<ul><li>90 Day Money Back Guarantee</li></ul>',
      },
      {
        id: ONE_TIME3,
        quantity: 3,
        title: '10% off',
        text: '3 Month One-Time Shipment',
        className: 'radio-block once',
        autoship: false,
        html: '<ul><li>90 Day Money Back Guarantee</li><li>Free Shipping</li></ul>',
      },
      {
        id: ONE_TIME6,
        quantity: 6,
        title: 'Additional 10% off',
        text: '6 Month One-Time Shipment',
        className: 'radio-block once',
        autoship: false,
        html: '<ul><li>90 Day Money Back Guarantee</li><li>Free Shipping</li></ul>',
      },
    ],
    []
  );

  useEffect(() => {
    if (selectedIndex === slides().length && hasSupplementImage) return;

    setSelectedIndex(selectedIndex);
  }, [selectedIndex, setSelectedIndex]);

  useEffect(() => {
    const handleCartChange = (beforeStateNull: boolean) => {
      const newDisabledSkus: Record<string, boolean> = {};

      const items = UltraCartService.getItems();

      items && items.forEach((item) => (newDisabledSkus[item.item_id] = true));

      setDisabledSkus(newDisabledSkus);
    };

    UltraCartService.subscribe(handleCartChange);

    return () => UltraCartService.unsubscribe(handleCartChange);
  }, []);

  const selectedVariant = () => {
    return orderType === ONE_TIME1 || orderType === ONE_TIME3 || orderType === ONE_TIME6
      ? oneTimeVariants[selectedIndex]
      : autoshipVariants[selectedIndex];
  };

  const variants = (orderTypeId?: string) => {
    const order = orderTypeId ? orderTypeId : orderType;
    const isOneTime = order === ONE_TIME1 || order === ONE_TIME3 || order === ONE_TIME6;

    return isOneTime ? oneTimeVariants : autoshipVariants;
  };

  const images = () => {
    return orderType === ONE_TIME1 || orderType === ONE_TIME3 || orderType === ONE_TIME6
      ? oneTimeImages
      : autoshipImages;
  };

  const slides = () => {
    return orderType === ONE_TIME1 || orderType === ONE_TIME3 || orderType === ONE_TIME6
      ? oneTimeSlides
      : autoshipSlides;
  };

  const onNewOrderType = (newOrderType: OrderType) => {
    if (newOrderType && prevOrderType.current === newOrderType.id) return;

    const variantsAfter = variants(newOrderType.id);

    const variantQuantityBefore = selectedVariant()?.skuId[0] ?? '0';

    const indexWithSameQuantity = variantsAfter
      .map((c) => c.skuId)
      .findIndex((d) => d[0] === variantQuantityBefore);

    if (newOrderType) setOrderType(newOrderType.id);

    if (indexWithSameQuantity > variantsAfter.length - 1 || indexWithSameQuantity === -1) {
      setSelectedIndex(0);
    } else setSelectedIndex(indexWithSameQuantity);

    prevVariantQuantity.current = variantQuantityBefore;

    prevOrderType.current = newOrderType.id;
  };

  const onAddToCart = (variant: ProductVariantType) => {
    const newItemToCart = variant;

    UltraCartService.addToCart(
      newItemToCart,
      () => {
        setDisabledSkus((prevDisabledSkus) => ({
          ...prevDisabledSkus,
          [newItemToCart.skuId]: true,
        }));
      },
      (error) => console.error(error)
    );
  };

  const onNewIndex = (i: number) => {
    const selectedSupplement = hasSupplementImage && slides().length - 1 === i;

    const selectedInstruction = hasSupplementImage && slides().length - 2 === i;

    if (selectedInstruction) {
      setShowInstructionImage(true);

      setShowSupplementImage(false);

      return;
    }

    if (selectedSupplement) {
      setShowSupplementImage(true);

      setShowInstructionImage(false);

      return;
    }

    setSelectedIndex(i);

    if (showSupplementImage) setShowSupplementImage(false);
    if (showInstructionImage) setShowInstructionImage(false);
  };

  const carouselElements = () => {
    const productImages = images();

    let img = productImages[selectedIndex];

    if (showSupplementImage) {
      img = productImages[productImages.length - 1];
    }

    if (showInstructionImage) {
      img = productImages[productImages.length - 2];
    }

    return slides().map((index) => (
      <div className={`carousel-main-slide product-block`} key={index}>
        <ProductImage
          raw={true}
          fill={false}
          quality={40}
          withLink={false}
          onSale={selectedVariant()?.isOnSale ?? false}
          image={img}
          className={`
          ${
            selectedIndex === index && isMobile
              ? styles.mobile.slider
              : selectedIndex === index && !isMobile
              ? styles.desktop.slider
              : ''
          }`}
          alt={product.title}
        />
      </div>
    ));
  };

  return (
    <section className="product-main-details">
      <div className="container">
        <div data-testid="product-images">
          <ImageCarousel
            slides={slides()}
            elements={carouselElements}
            options={options}
            images={images()}
            selectedIndexOut={selectedIndex}
            onNewIndex={onNewIndex}
            showThumbs={true}
          />
          {isMobile ? (
            ''
          ) : (
            <ProductAllergens allergens={product?.allergens ?? []} productName={product.title} />
          )}
        </div>
        <div className="product-functions" data-testid="product-order-item">
          <ProductOrderForm
            setSelectedIndex={setSelectedIndex}
            selectedVariantIndex={selectedIndex}
            onNewSelectedOrderType={onNewOrderType}
            onNewQuantity={setQuantity}
            product={product}
            productVariants={oneTimeVariants}
            productAutoshipVariants={autoshipVariants}
            orderTypes={orderTypes}
            defaultOrderType={orderTypes[0]}
            defaultOneTimePrice={defaultOneTimeVariant?.price ?? product.price}
            defaultAutoShipPrice={defaultAutoshipVariant?.price ?? product.price}
            onAddCart={onAddToCart}
            addCartDisabled={disabledSkus[selectedVariant()?.skuId ?? '']}
          />
        </div>
      </div>
    </section>
  );
};

export default ProductOrderController;
